import { useRef, useState } from 'react'
import { Button } from '@mui/material'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import { adminService } from '../services/adminService'
import { apiError } from './errors'
import { useToast } from './useToast'

/**
 * Uploads a file to the event's media store and returns the served URL
 * (/api/assets/{id}) via onUploaded. Disabled until the event exists (needs an
 * eventId).
 */
export interface UploadedAssetMeta {
  id: string
  filename: string
  contentType: string
  byteSize: number
}

export default function AssetUploadButton({
  eventId,
  accept = 'image/*',
  label = 'Upload',
  onUploaded,
  uploader,
}: {
  eventId?: string
  accept?: string
  label?: string
  onUploaded: (url: string, meta: UploadedAssetMeta) => void
  /** Optional custom uploader (e.g. the video-recording endpoint). Defaults to
   *  the standard image/PDF asset upload. */
  uploader?: (eventId: string, file: File) => Promise<UploadedAssetMeta & { url: string }>
}) {
  const { notify } = useToast()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  const pick = () => inputRef.current?.click()

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !eventId) return
    setBusy(true)
    try {
      const res = await (uploader
        ? uploader(eventId, file)
        : adminService.uploadAsset(eventId, file))
      onUploaded(res.url, {
        id: res.id,
        filename: res.filename,
        contentType: res.contentType,
        byteSize: res.byteSize,
      })
      notify('Uploaded')
    } catch (err) {
      notify(apiError(err), 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={onChange}
      />
      <Button
        variant="outlined"
        startIcon={<UploadFileRoundedIcon />}
        onClick={pick}
        disabled={busy || !eventId}
      >
        {busy ? 'Uploading…' : label}
      </Button>
    </>
  )
}
